import { Home, Building2, Landmark, Scale, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const Card = ({ id, icon: Icon, title, copy, list, className = "", tall = false }) => (
  <article
    data-testid={`service-card-${id}`}
    className={`group relative flex h-full flex-col rounded-2xl border border-ink/10 bg-cream p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-28px_rgba(52,59,65,0.35)] lg:p-10 ${className}`}
  >
    <div className="flex items-start justify-between">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-ink/10 text-ink transition-colors duration-500 group-hover:border-eucalyptus group-hover:bg-eucalyptus group-hover:text-ivory">
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </span>
    </div>
    <h3 className={`mt-8 font-display font-medium text-ink ${tall ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`}>
      {title}
    </h3>
    <p className={`mt-4 leading-relaxed text-ink/65 ${tall ? "max-w-md text-base sm:text-lg" : "text-base"}`}>
      {copy}
    </p>
    {list && (
      <ul className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 sm:max-w-sm">
        {list.map((item) => (
          <li key={item} className="flex items-center gap-3 text-sm text-ink/70">
            <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-eucalyptus" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    )}
    <span className="mt-auto pt-8">
      <ArrowUpRight className="h-5 w-5 text-ink/25 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-eucalyptus" />
    </span>
  </article>
);

const Services = () => (
  <section id="services" className="bg-ivory py-24 text-ink lg:py-32">
    <div className="mx-auto max-w-[88rem] px-6 lg:px-12">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <Reveal>
          <Eyebrow>What we do</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Lending, shaped
            <br />
            around <span className="italic text-eucalyptus">you.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="md:max-w-sm md:pb-2">
          <p className="leading-relaxed text-ink/65">
            No jargon, no spin — home loan reviews, refinancing and new loans
            that suit your needs and your lifestyle, whatever the move.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12 lg:mt-20">
        <Reveal className="md:col-span-6" delay={0.05}>
          <Card
            id="residential"
            tall
            icon={Home}
            title="Residential"
            copy="Your home loan, covered at every stage — and revisited whenever life changes."
            list={["Purchase", "Refinance", "Top Up", "Construction", "Restructuring"]}
          />
        </Reveal>
        <Reveal className="md:col-span-6" delay={0.12}>
          <Card
            id="commercial"
            tall
            icon={Building2}
            title="Commercial"
            copy="Similar to residential, for business — whether owner-occupied or investor."
            list={["Purchase", "Refinance", "Top Up", "Construction", "Restructuring"]}
          />
        </Reveal>
        <Reveal className="md:col-span-6" delay={0.08}>
          <Card
            id="smsf"
            icon={Landmark}
            title="SMSF"
            copy="Lending for property held within your self-managed super fund — structured with care."
          />
        </Reveal>
        <Reveal className="md:col-span-6" delay={0.15}>
          <Card
            id="toro-standard"
            tall
            icon={Scale}
            title="The Toro Standard"
            copy="Every loan weighed on three fronts — the rate you pay, the product you hold, and the service you feel."
          />
        </Reveal>
      </div>
    </div>
  </section>
);

export default Services;
